import { useState, useRef } from "react";
import { fmt } from "../utils/fmt";
import { RESEARCH_TREE } from "../data/research";
import { DEBRIS_ITEMS, BASE_MATERIALS } from "../data/items";
import { currentLevel, maxLevel, prereqsMet } from "../hooks/useResearch";

// ── Tree definitions ──────────────────────────────────────────────────────────
// Each tree declares its nodes in column order (left→right = early→late game).
// Rows within a column are stacked top→bottom.
const TREES = [
  {
    id: "mining", label: "MINING", icon: "⚡", color: "#00ccff",
    columns: [
      ["dmg1", "rng1", "spd1", "loot1"],
      ["width1", "attract1", "smelt1", "spawn1"],
      ["auto1", "smeltYield1", "debrisCap1"],
      ["autopilot", "smeltCost1"],
      ["drones"],
    ],
  },
  {
    id: "forge", label: "FORGE", icon: "🔥", color: "#ff9944",
    columns: [
      ["refinery"],
      ["queue", "batch1"],
    ],
  },
  {
    id: "combat", label: "COMBAT", icon: "⚔️", color: "#ff4455",
    columns: [
      ["hull1", "engine1", "blaster1"],
      ["shield1", "firerate1"],
      ["firewall_patch", "multishot1", "ai_tracking"],
      ["orbital_drill"],
    ],
  },
  {
    id: "starmap", label: "STARMAP", icon: "🗺️", color: "#33dd99",
    columns: [
      ["nav1"],
      ["fuel_efficiency", "node_scanner"],
      ["sector_diversity"],
    ],
  },
  {
    id: "xp", label: "XP", icon: "⭐", color: "#bb99ff",
    columns: [
      ["xp_boost"],
      ["xp_miner", "xp_hunter"],
      ["xp_levelup"],
    ],
  },
  {
    id: "echoes", label: "ECHOES", icon: "🌀", color: "#aa55ff",
    columns: [
      ["echoFrequency", "echoQuantity", "echoCardBoost"],
      ["activeCardSlots"],
      ["trade_refresh", "trade_access"],
      ["trade_discount", "trade_stock"],
      ["trade_sell"],
      ["trade_discovery"],
    ],
  },
];

const NODE_ICONS = {
  refinery:"⚗️", nav1:"🗺️", queue:"📋", batch1:"⚙️",
  dmg1:"⚡", rng1:"🔭", width1:"〰️", spd1:"🔄",
  attract1:"🧲", auto1:"🤖", smelt1:"🔥", loot1:"📡",
  spawn1:"☄️", debrisCap1:"💥", autopilot:"🧭", drones:"🛸",
  hull1:"🛡️", shield1:"🔰", engine1:"🚀", blaster1:"🔫",
  firerate1:"⚡", multishot1:"🎯", ai_tracking:"🎯",
  firewall_patch:"🔒", orbital_drill:"🌀",
  fuel_efficiency:"⛽", node_scanner:"📡", sector_diversity:"🗺️",
  xp_boost:"🧠", xp_miner:"⛏️", xp_hunter:"🎯", xp_levelup:"📈",
  echoFrequency:"〰️", echoQuantity:"💠", echoCardBoost:"✨", activeCardSlots:"🃏",
  trade_access:"🏪", trade_refresh:"🔄", trade_discount:"💰",
  trade_sell:"📈", trade_stock:"📦", trade_discovery:"🔍",
  smeltYield1:"📈", smeltCost1:"⚗️",
};

// ── Node state helper ─────────────────────────────────────────────────────────
function nodeStatus(entry, researchLevels, discoveredOutposts = 0) {
  const cur  = currentLevel(researchLevels, entry.id);
  const max  = maxLevel(entry);
  const meet = prereqsMet(researchLevels, entry, discoveredOutposts);
  if (cur >= max)  return "maxed";
  if (!meet)       return "locked";
  if (cur > 0)     return "partial";
  return "available";
}

// ── Small tree node (circle) ──────────────────────────────────────────────────
function TreeNode({ entry, color, researchLevels, discoveredOutposts, isSelected, onClick }) {
  const status = nodeStatus(entry, researchLevels, discoveredOutposts);
  const cur    = currentLevel(researchLevels, entry.id);
  const max    = maxLevel(entry);
  const pct    = max > 0 ? cur / max : 0;

  const bgColor = status === "maxed"     ? "#1a3a22"
                : status === "locked"    ? "rgba(255,255,255,0.04)"
                : status === "partial"   ? `${color}22`
                : `${color}18`;

  const borderColor = status === "maxed"     ? "#44dd88"
                    : status === "locked"    ? "rgba(255,255,255,0.12)"
                    : isSelected             ? "#fff"
                    : color;

  const borderWidth = isSelected ? 2 : 1.5;

  return (
    <button
      onClick={() => status !== "locked" && onClick(entry)}
      title={entry.name}
      style={{
        width: 52, height: 52, borderRadius: 12, flexShrink: 0,
        background: bgColor,
        border: `${borderWidth}px solid ${borderColor}`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: status === "locked" ? "default" : "pointer",
        opacity: status === "locked" ? 0.4 : 1,
        position: "relative",
        boxShadow: isSelected ? `0 0 0 2px ${color}66` : "none",
        transition: "all 0.15s",
        fontFamily: "inherit",
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>
        {status === "locked" ? "🔒" : (NODE_ICONS[entry.id] ?? "◆")}
      </span>
      {max > 1 && (
        <div style={{
          position: "absolute", bottom: 3, left: 4, right: 4,
          height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden",
        }}>
          <div style={{ height: "100%", width: `${pct * 100}%`, background: status === "maxed" ? "#44dd88" : color, borderRadius: 2 }} />
        </div>
      )}
      {status === "maxed" && (
        <div style={{
          position: "absolute", top: -5, right: -5,
          width: 14, height: 14, borderRadius: "50%",
          background: "#44dd88", border: "1.5px solid #0a1a10",
          fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900,
        }}>✓</div>
      )}
      {status !== "maxed" && cur > 0 && max > 1 && (
        <div style={{
          position: "absolute", top: -5, right: -5,
          width: 16, height: 14, borderRadius: 4,
          background: color, border: "1px solid rgba(0,0,0,0.4)",
          fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#000", fontWeight: 900,
          paddingHorizontal: 2,
        }}>{cur}</div>
      )}
    </button>
  );
}

// ── Connector line between two nodes ─────────────────────────────────────────
// Draws a horizontal line in CSS between columns when a prereq exists.
// We render connectors as absolute-positioned divs within the tree canvas.

// ── Detail panel shown when a node is selected ───────────────────────────────
function DetailPanel({ entry, color, researchLevels, inventory, materials, doResearch, discoveredOutposts }) {
  if (!entry) return (
    <div style={{ padding: "14px 16px", color: "rgba(255,255,255,0.25)", fontSize: 12, textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      Tap a node to view details
    </div>
  );

  const cur     = currentLevel(researchLevels, entry.id);
  const max     = maxLevel(entry);
  const status  = nodeStatus(entry, researchLevels, discoveredOutposts);
  const maxed   = status === "maxed";
  const locked  = status === "locked";

  const next    = !maxed ? entry.levels[cur] : null;
  const isRaw   = next?.costIsRaw ?? entry.costIsRaw ?? false;
  const src     = isRaw ? inventory : materials;
  const affordable = next ? next.cost.every(c => (src[c.id] ?? 0) >= c.qty) : false;
  const outpostOk  = entry.minOutposts == null || discoveredOutposts >= entry.minOutposts;
  const canBuy  = !maxed && !locked && affordable && outpostOk;

  const pct = max > 1 ? (cur / max) * 100 : cur >= 1 ? 100 : 0;

  // Prereq list for display
  const prereqEntries = (entry.req ?? []).map(r => {
    const e = RESEARCH_TREE.find(x => x.id === r.id);
    return e ? { name: e.name, required: r.minLevel, have: currentLevel(researchLevels, r.id) } : null;
  }).filter(Boolean);

  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", background: "rgba(4,8,18,0.97)", flexShrink: 0 }}>
      <div style={{ padding: "10px 14px 12px" }}>
        {/* Node header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 9, flexShrink: 0,
            background: `${color}18`, border: `1.5px solid ${color}55`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          }}>
            {NODE_ICONS[entry.id] ?? "◆"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: maxed ? "#44dd88" : color, marginBottom: 1 }}>
              {entry.name}
              {entry.badge && <span style={{ marginLeft: 6, fontSize: 8, fontWeight: 700, letterSpacing: 1, color: `${color}88`, background: `${color}18`, padding: "1px 4px", borderRadius: 3 }}>{entry.badge}</span>}
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", lineHeight: 1.35 }}>{entry.desc}</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: maxed ? "#44dd88" : "rgba(255,255,255,0.45)", flexShrink: 0 }}>
            {maxed ? "MAX" : max === 1 ? (cur ? "✓" : "—") : `${cur}/${max}`}
          </div>
        </div>

        {/* Progress bar */}
        {max > 1 && (
          <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginBottom: 8 }}>
            <div style={{ height: "100%", width: `${pct}%`, borderRadius: 2, background: maxed ? "#44dd88" : color, transition: "width 0.3s" }} />
          </div>
        )}

        {/* Next level label */}
        {next?.label && (
          <div style={{ fontSize: 11, color: `${color}cc`, marginBottom: 8, fontStyle: "italic" }}>
            → {next.label}
          </div>
        )}

        {/* Prereqs not met */}
        {locked && prereqEntries.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
            {prereqEntries.map(p => (
              <div key={p.name} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "rgba(255,80,80,0.12)", border: "1px solid rgba(255,80,80,0.25)", color: p.have >= p.required ? "#44dd88" : "#ff6655" }}>
                {p.name} {p.have}/{p.required}
              </div>
            ))}
          </div>
        )}

        {/* Cost + buy */}
        {!maxed && next && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", flex: 1 }}>
              {next.cost.map((c, ci) => {
                const items = isRaw ? DEBRIS_ITEMS : BASE_MATERIALS;
                const m     = items.find(mm => mm.id === c.id);
                const have  = src[c.id] ?? 0;
                const enough= have >= c.qty;
                return (
                  <div key={ci} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11 }}>
                    <span style={{ fontSize: 12 }}>{m?.icon ?? "?"}</span>
                    <span style={{ fontWeight: 700, color: enough ? (m?.color ?? "#aaa") : "#ff5555" }}>
                      {fmt(have)}<span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>/{fmt(c.qty)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => canBuy && doResearch(entry.id)}
              disabled={!canBuy}
              style={{
                flexShrink: 0, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 800,
                letterSpacing: 0.5, cursor: canBuy ? "pointer" : "default", fontFamily: "inherit",
                background: canBuy ? `${color}25` : "rgba(255,255,255,0.04)",
                border: `1px solid ${canBuy ? color : "rgba(255,255,255,0.1)"}`,
                color: canBuy ? color : "rgba(255,255,255,0.2)",
                transition: "all 0.15s",
              }}
            >
              {!outpostOk ? `🏪 ${discoveredOutposts}/${entry.minOutposts}` : locked ? "LOCKED" : !affordable ? "COST" : max > 1 ? `LV ${cur + 1}` : "UNLOCK"}
            </button>
          </div>
        )}

        {maxed && (
          <div style={{ fontSize: 11, color: "#44dd88", fontWeight: 700 }}>✓ Fully researched</div>
        )}
      </div>
    </div>
  );
}

// ── Tree panel ────────────────────────────────────────────────────────────────
function TreePanel({ tree, researchLevels, discoveredOutposts, selectedId, onSelect }) {
  const scrollRef = useRef(null);
  const COL_W = 68;  // node width + gap
  const ROW_H = 64;  // node height + gap

  // Flatten tree to get node positions
  const nodePositions = {}; // id → {col, row}
  tree.columns.forEach((col, ci) => {
    col.forEach((id, ri) => { nodePositions[id] = { col: ci, row: ri }; });
  });

  const maxRows = Math.max(...tree.columns.map(c => c.length));
  const canvasW = tree.columns.length * COL_W + 16;
  const canvasH = maxRows * ROW_H + 8;

  // Build connector lines: for each node, draw line from each prereq
  const connectors = [];
  tree.columns.forEach((col, ci) => {
    col.forEach(id => {
      const entry = RESEARCH_TREE.find(r => r.id === id);
      if (!entry) return;
      (entry.req ?? []).forEach(req => {
        const from = nodePositions[req.id];
        const to   = nodePositions[id];
        if (!from || !to) return;
        const fromCur  = currentLevel(researchLevels, req.id);
        const prereqOk = fromCur >= req.minLevel;
        connectors.push({ from, to, prereqOk, id: `${req.id}-${id}` });
      });
    });
  });

  return (
    <div ref={scrollRef} style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
      <div style={{ position: "relative", width: canvasW, height: canvasH, minWidth: canvasW }}>
        {/* SVG connectors */}
        <svg style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
          width={canvasW} height={canvasH}>
          {connectors.map(({ from, to, prereqOk, id }) => {
            const x1 = from.col * COL_W + 34;
            const y1 = from.row * ROW_H + 26 + 4;
            const x2 = to.col   * COL_W + 18;
            const y2 = to.row   * ROW_H + 26 + 4;
            const mx = (x1 + x2) / 2;
            return (
              <path key={id}
                d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                stroke={prereqOk ? `${tree.color}66` : "rgba(255,255,255,0.1)"}
                strokeWidth={1.5}
                fill="none"
                strokeDasharray={prereqOk ? "none" : "4 3"}
              />
            );
          })}
        </svg>

        {/* Nodes */}
        {tree.columns.map((col, ci) =>
          col.map((id, ri) => {
            const entry = RESEARCH_TREE.find(r => r.id === id);
            if (!entry) return null;
            return (
              <div key={id} style={{
                position: "absolute",
                left: ci * COL_W + 8,
                top:  ri * ROW_H + 4,
              }}>
                <TreeNode
                  entry={entry}
                  color={tree.color}
                  researchLevels={researchLevels}
                  discoveredOutposts={discoveredOutposts}
                  isSelected={selectedId === id}
                  onClick={onSelect}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Material bar ──────────────────────────────────────────────────────────────
function MaterialBar({ inventory, materials }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 8px", justifyContent: "flex-end" }}>
      {BASE_MATERIALS.map(bm => (
        <div key={bm.id} style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10, opacity: (materials[bm.id] || 0) > 0 ? 1 : 0.2 }}>
          <span style={{ fontSize: 11 }}>{bm.icon}</span>
          <span style={{ color: bm.color, fontWeight: 700 }}>{fmt(materials[bm.id] || 0)}</span>
        </div>
      ))}
      {DEBRIS_ITEMS.filter(di => (inventory[di.id] || 0) > 0).map(di => (
        <div key={di.id} style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10 }}>
          <span style={{ fontSize: 11 }}>{di.icon}</span>
          <span style={{ color: di.color, fontWeight: 700 }}>{fmt(inventory[di.id] || 0)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ResearchTab({ inventory, materials, researchLevels, doResearch, discoveredTradeOutposts = 0 }) {
  const [activeTree, setActiveTree] = useState("mining");
  const [selectedId, setSelectedId] = useState(null);

  const tree = TREES.find(t => t.id === activeTree) ?? TREES[0];
  const selectedEntry = selectedId ? RESEARCH_TREE.find(r => r.id === selectedId) : null;

  function handleSelect(entry) {
    setSelectedId(prev => prev === entry.id ? null : entry.id);
  }

  // Progress summary per tree
  function treeProgress(t) {
    const ids = t.columns.flat();
    const entries = ids.map(id => RESEARCH_TREE.find(r => r.id === id)).filter(Boolean);
    const total = entries.reduce((s, e) => s + maxLevel(e), 0);
    const done  = entries.reduce((s, e) => s + Math.min(currentLevel(researchLevels, e.id), maxLevel(e)), 0);
    return { done, total };
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ padding: "6px 12px 5px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 11, letterSpacing: 1, color: "rgba(190,130,255,0.9)", fontWeight: 700 }}>RESEARCH</div>
        <MaterialBar inventory={inventory} materials={materials} />
      </div>

      {/* Tree selector tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0, overflowX: "auto" }}>
        {TREES.map(t => {
          const { done, total } = treeProgress(t);
          const active = t.id === activeTree;
          return (
            <button key={t.id} onClick={() => { setActiveTree(t.id); setSelectedId(null); }}
              style={{
                flex: "0 0 auto", padding: "7px 10px 8px",
                background: active ? `${t.color}12` : "transparent",
                border: "none", borderBottom: `2px solid ${active ? t.color : "transparent"}`,
                color: active ? t.color : "rgba(255,255,255,0.35)",
                fontFamily: "inherit", fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                cursor: "pointer", transition: "all 0.15s",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              }}
            >
              <span style={{ fontSize: 14 }}>{t.icon}</span>
              <span>{t.label}</span>
              <span style={{ fontSize: 9, opacity: 0.6 }}>{done}/{total}</span>
            </button>
          );
        })}
      </div>

      {/* Tree canvas */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 10px 4px", WebkitOverflowScrolling: "touch" }}>
        <TreePanel
          key={activeTree}
          tree={tree}
          researchLevels={researchLevels}
          discoveredOutposts={discoveredTradeOutposts}
          selectedId={selectedId}
          onSelect={handleSelect}
        />
      </div>

      {/* Detail panel */}
      <DetailPanel
        entry={selectedEntry}
        color={tree.color}
        researchLevels={researchLevels}
        inventory={inventory}
        materials={materials}
        doResearch={doResearch}
        discoveredOutposts={discoveredTradeOutposts}
      />

    </div>
  );
}
